/**
 * Minimal zero-dependency promise concurrency limiter (p-limit style).
 *
 * feat-085 Phase-2 (P2-B): the Pilot Dashboard used to fire all of its endpoints
 * (scorecard + trend + per-store + adjustments) at once. Combined with the backend
 * query fan-out (ADR 0013a) that burst saturated the DB connection pool and 504'd
 * the page. This caps the number of in-flight pilot requests so the dashboard never
 * issues more than `maxConcurrent` at a time; excess calls queue FIFO and run as
 * slots free up. Pairs with the component-level progressive load (headline first,
 * heavy panels deferred until revealed) to keep the initial burst tiny.
 */

export type Limiter = <T>(task: () => Promise<T>) => Promise<T>;

export function createLimiter(maxConcurrent: number): Limiter {
  if (!Number.isInteger(maxConcurrent) || maxConcurrent < 1) {
    throw new Error('createLimiter: maxConcurrent must be an integer >= 1');
  }

  let active = 0;
  const queue: Array<() => void> = [];

  const drain = () => {
    while (active < maxConcurrent && queue.length > 0) {
      const run = queue.shift()!;
      active++;
      run();
    }
  };

  return function limit<T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      queue.push(() => {
        // Defer to a microtask so a synchronously-throwing `task` rejects the
        // returned promise rather than escaping, and so `active--` always runs.
        Promise.resolve()
          .then(task)
          .then(resolve, reject)
          .finally(() => {
            active--;
            drain();
          });
      });
      drain();
    });
  };
}
